import { Activity, BarChart3, Database, FolderKanban, Gavel, House, Landmark, MessageCircleQuestion, Newspaper, Scale, ScrollText, Send, ShieldCheck, SlidersHorizontal, Sparkles } from "lucide-react";
import { useCallback } from "react";
import { Outlet, ScrollRestoration, useLocation, useNavigate, useOutlet } from "react-router";
import { useAuth, useHandoff, usePermission } from "@ncp/api-client";
import { AppVersion, ReportIssue, SessionGuard } from "@ncp/assistant-sdk";
import { useLocale, useTranslation } from "@ncp/i18n";
import { ActionScope, AppShell, ConfirmProvider, GovBar, PageTransition, SideNav, Toaster, useSidebarCollapsed, type NavItem, type NavSection } from "@ncp/ui";
import { screenIcon, useScreenMenu } from "@ncp/screens";
import { AccountMenu } from "../components/AccountMenu";
import { GlobalSearch } from "../components/GlobalSearch";
import { ScrollToTop } from "../components/ScrollToTop";
import { MethodConfidenceScale } from "../features/case/MethodInfo";
import { adminHref, assistantHref } from "../lib/links";
import { DECIDER_ROLES, hasAnyRole } from "../lib/roles";

const SIDEBAR_STORAGE_KEY = "ncp.backoffice.sidebar";

/** Outermost route element: scroll handling has to cover the login page as well as the staff shell. */
export function RootLayout() {
  return (
    <>
      <ScrollToTop />
      <ScrollRestoration />
      <Outlet />
    </>
  );
}

export function BackofficeLayout() {
  const { t } = useTranslation();
  const handoff = useHandoff();
  const { locale, toggle, dir } = useLocale();
  const { user, clear } = useAuth();
  const { permissions } = usePermission();
  const canOpenConsole = permissions.some((p) => p === "*" || p.startsWith("platform."));
  const canOpenAssistant = permissions.some((p) => p === "*" || p === "assistant.use");
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // The element is taken per route so the page fading out keeps rendering itself; a live <Outlet /> there would mount the incoming page twice.
  const outlet = useOutlet();

  const go = (to: string) => () => navigate(to);
  const signedOut = useCallback(() => navigate("/login"), [navigate]);
  const active = (to: string) => (to === "/" ? pathname === "/" : pathname.startsWith(to));

  const [collapsed, toggleCollapsed] = useSidebarCollapsed(SIDEBAR_STORAGE_KEY);

  const roles = user?.roles ?? [];
  const seesDecisions = hasAnyRole(roles, [...DECIDER_ROLES, "CommitteeMember"]);
  const screens = useScreenMenu("backoffice");
  const item = (key: string, label: string, icon: NavItem["icon"], to: string, extra?: Partial<NavItem>): NavItem =>
    ({ key, label, icon, href: to, active: active(to), onClick: go(to), ...extra });

  // The menu names each process by what it holds (visual items 14, 7.27); the assistant is a resource, not a setting.
  const sections: NavSection[] = [
    {
      items: [
        item("home", t("nav.commandCenter"), House, "/", { active: pathname === "/" || pathname.startsWith("/inbox") }),
        item("statistics", t("nav.statistics"), BarChart3, "/statistics"),
      ],
    },
    {
      title: t("nav.work"),
      items: [
        item("concentration", t("nav.economicConcentration"), FolderKanban, "/concentration"),
        item("investigations", t("nav.investigationsComplaints"), Activity, "/investigations"),
        item("advocacy", t("nav.advocacy"), Landmark, "/advocacy"),
        item("exemptions", t("nav.exemptions"), ShieldCheck, "/exemptions"),
        item("grievances", t("nav.grievances"), Scale, "/grievances"),
        item("enquiries", t("nav.legalEnquiriesInternal"), MessageCircleQuestion, "/legal-enquiries"),
        ...(seesDecisions ? [item("decisions", t("nav.decisions"), Gavel, "/decisions")] : []),
        item("outside-requests", t("nav.outsideRequests"), Send, "/outside-requests"),
      ],
    },
    ...(screens.data?.length
      ? [{ title: t("nav.configured"), items: screens.data.map((s) => item(`screen-${s.key}`, locale === "ar" && s.nameAr ? s.nameAr : s.name, screenIcon(s.icon), `/s/${s.key}`)) }]
      : []),
    {
      title: t("nav.resources"),
      items: [
        item("databank", t("nav.dataBank"), Database, "/data-bank"),
        item("news", t("nav.news"), Newspaper, "/news"),
        item("legal", t("nav.legalKnowledge"), ScrollText, "/legal-knowledge"),
        // Chat with the departmental assistants lives in the AI Workspace (apps/assistant); it opens in a new tab so the officer keeps their place.
        ...(canOpenAssistant ? [{ key: "assistant", label: t("nav.aiWorkspace"), icon: Sparkles, href: assistantHref("/"), active: false, onClick: () => handoff.open(assistantHref("/")) }] : []),
      ],
    },
    // Workflow Studio, Integrations, AI Governance and Audit & Security are in the Platform Console (apps/admin), also in a new tab.
    ...(canOpenConsole
      ? [{ title: t("nav.settings"), items: [{ key: "console", label: t("nav.platformConsole"), icon: SlidersHorizontal, href: adminHref("/"), active: false, onClick: () => handoff.open(adminHref("/")) }] }]
      : []),
  ];

  const name = locale === "ar" && user?.displayNameAr ? user.displayNameAr : user?.displayName ?? "";
  const title = locale === "ar" && user?.titleAr ? user.titleAr : user?.title ?? "";

  return (
    <>
      <AppShell
        header={
          <GovBar
            ministryName={t("app.ministry")}
            ministryNameSecondary={t("app.department")}
            platformName={t("app.backofficeTitle")}
            locale={locale}
            onToggleLocale={toggle}
            logo={{ src: locale === "ar" ? `${import.meta.env.BASE_URL}brand/moet-logo-ar.png` : `${import.meta.env.BASE_URL}brand/moet-logo-en.png`, alt: t("app.ministry") }}
            onHome={go("/")}
            search={user ? <GlobalSearch /> : null}
            right={user ? <ReportIssue app="backoffice" labelled /> : null}
            account={user ? <AccountMenu name={name} title={title} onSignOut={() => { clear(); navigate("/login"); }} /> : null}
          />
        }
        collapsed={collapsed}
        sidebarWidth="w-[17.5rem]"
        sidebar={<SideNav sections={sections} collapsed={collapsed} onToggleCollapsed={toggleCollapsed} footer={collapsed ? null : <AppVersion />} />}
        mobileSidebar={<SideNav sections={sections} footer={<AppVersion autoOpen={false} />} />}
      >
        <ConfirmProvider>
          <MethodConfidenceScale>
            <PageTransition routeKey={pathname}>
              {/* Every screen has at most one primary action, marked where it is rendered; everything else reads as secondary. */}
              <ActionScope emphasis="secondary">
                {outlet}
              </ActionScope>
            </PageTransition>
          </MethodConfidenceScale>
        </ConfirmProvider>
      </AppShell>
      <SessionGuard onSignedOut={signedOut} />
      <Toaster dir={dir} />
    </>
  );
}
